import { z } from 'zod';

/**
 * Supported event types for tracking
 */
export enum EventType {
  // Action events
  ACTION_VIEWED = 'ACTION_VIEWED',
  ACTION_RENDERED = 'ACTION_RENDERED',
  ACTION_INTERACTED = 'ACTION_INTERACTED',
  ACTION_REJECTED_REASON_PROVIDED = 'ACTION_REJECTED_REASON_PROVIDED',
  ACTION_EDITED = 'ACTION_EDITED',

  STRIPE_CONNECT_ONBOARDING_STARTED = 'STRIPE_CONNECT_ONBOARDING_STARTED',

  ENCOUNTER_SUBMITTED = 'ENCOUNTER_SUBMITTED',

  // Generic events
  FEEDBACK_SUBMITTED = 'FEEDBACK_SUBMITTED',
  EXPRESSED_INTEREST = 'EXPRESSED_INTEREST',
  EXPRESSED_NOT_INTERESTED = 'EXPRESSED_NOT_INTERESTED',

  // Document events
  PDF_INTERACTED = 'PDF_INTERACTED',

  // Toolbar events
  TOOLBAR_CLICKED = 'TOOLBAR_CLICKED',

  // Add more event types as needed...
}

/**
 * Interaction types for ACTION_INTERACTED events
 */
export enum InteractionType {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  UPDATE = 'UPDATE',
  EXPAND = 'EXPAND',
  COLLAPSE = 'COLLAPSE',
}

// Base schema for all action-related events
export const BaseActionSchema = z.object({
  action_id: z.string(),
  action_type: z.string(),
  encounter_id: z.string().optional().nullable(),
  patient_id: z.string().optional().nullable(),
});

// Zod schema for ACTION_VIEWED event data - extends the base schema
export const ActionViewedSchema = BaseActionSchema;

// Zod schema for ACTION_RENDERED event data - extends the base schema
export const ActionRenderedSchema = BaseActionSchema;

// Zod schema for ACTION_INTERACTED event data - extends the base schema and adds interaction type
export const ActionInteractedSchema = BaseActionSchema.extend({
  interaction_type: z.nativeEnum(InteractionType),
});

// Zod schema for ACTION_REJECTED_REASON_PROVIDED event data - extends the base schema and adds rejection reason
export const ActionRejectedSchema = BaseActionSchema.extend({
  rejection_reason: z.string(), // e.g., "wrong_suggestion" | "dont_care_for_feature" | "other"
  feedback: z.string().optional().nullable(), // additional comment if provided
});

// Zod schema for ACTION_EDITED event data - tracks when users edit actions
export const ActionEditedSchema = BaseActionSchema.extend({
  context: z.string(), // e.g., "rejection_modal", "card"
  original_action_payload: z.any(),
  updated_action_payload: z.any(),
});

// Zod schema for ENCOUNTER_SUBMITTED event data
export const CreateNoteFromEncounterButtonClickedSchema = z.object({
  encounter_id: z.string(),
  click_context: z.string(),
});

// Zod schema for FEEDBACK_SUBMITTED event data - Generic feedback event
export const FeedbackSubmittedSchema = z.object({
  feedback_type: z.enum(['thumbs_up', 'thumbs_down']),
  context: z.record(z.any()), // Flexible metadata about what the feedback is for
  comment: z.string().optional().nullable(),
  session_id: z.string().optional().nullable(),
});

// Zod schema for STRIPE_CONNECT_ONBOARDING_STARTED event data - empty object as requested
export const StripeConnectOnboardingStartedSchema = z.object({});

// Zod schema for EXPRESSED_INTEREST event data - tracks fake door test clicks
export const ExpressedInterestSchema = z.object({
  feature: z.string(), // e.g., 'communications', 'reports_analytics', 'bill_insurance'
});

// Zod schema for EXPRESSED_NOT_INTERESTED event data - tracks when users dismiss or decline features
export const ExpressedNotInterestedSchema = z.object({
  feature: z.string(), // e.g., 'eligibility-checks', 'jotcoach', etc.
});

// Zod schema for PDF_INTERACTED event data
export const PDFInteractedSchema = z.object({
  interaction_type: z.enum(['download', 'view', 'print', 'save_to_portal']),

  // User info
  user_type: z.enum(['provider', 'patient', 'staff', 'anonymous']),
  patient_id: z.string().optional().nullable(),

  // Access details
  access_method: z.enum([
    'preview_modal',
    'direct_link',
    'patient_portal',
    'provider_portal',
    'email_link',
  ]),
  user_agent: z.string().optional().nullable(),

  // Document info
  document_type: z.string().optional().nullable(), // "lab_order", "note", etc.
  document_id: z.string().optional().nullable(),
});

// Zod schema for TOOLBAR_CLICKED event data
export const ToolbarClickedSchema = z.object({
  feature: z.string(), // e.g., 'lab_order', 'payment_request', etc.
  user_agent: z.string().optional().nullable(),
  metadata: z.record(z.any()).optional(), // Feature-specific data (e.g., patient_id for lab_order)
});

// Map event types to their schemas
export const EVENT_SCHEMAS = {
  [EventType.ACTION_VIEWED]: ActionViewedSchema,
  [EventType.ACTION_RENDERED]: ActionRenderedSchema,
  [EventType.ACTION_INTERACTED]: ActionInteractedSchema,
  [EventType.ACTION_REJECTED_REASON_PROVIDED]: ActionRejectedSchema,
  [EventType.ACTION_EDITED]: ActionEditedSchema,
  [EventType.STRIPE_CONNECT_ONBOARDING_STARTED]: StripeConnectOnboardingStartedSchema,
  [EventType.ENCOUNTER_SUBMITTED]: CreateNoteFromEncounterButtonClickedSchema,
  [EventType.FEEDBACK_SUBMITTED]: FeedbackSubmittedSchema,
  [EventType.EXPRESSED_INTEREST]: ExpressedInterestSchema,
  [EventType.EXPRESSED_NOT_INTERESTED]: ExpressedNotInterestedSchema,
  [EventType.PDF_INTERACTED]: PDFInteractedSchema,
  [EventType.TOOLBAR_CLICKED]: ToolbarClickedSchema,
} as const;

// Infer TypeScript types from Zod schemas
export type ActionViewedEvent = z.infer<typeof ActionViewedSchema>;
export type ActionRenderedEvent = z.infer<typeof ActionRenderedSchema>;
export type ActionInteractedEvent = z.infer<typeof ActionInteractedSchema>;
export type ActionRejectedEvent = z.infer<typeof ActionRejectedSchema>;
export type ActionEditedEvent = z.infer<typeof ActionEditedSchema>;
export type StripeConnectOnboardingStartedEvent = z.infer<
  typeof StripeConnectOnboardingStartedSchema
>;
export type CreateNoteFromEncounterButtonClickedEvent = z.infer<
  typeof CreateNoteFromEncounterButtonClickedSchema
>;
export type FeedbackSubmittedEvent = z.infer<typeof FeedbackSubmittedSchema>;
export type ExpressedInterestEvent = z.infer<typeof ExpressedInterestSchema>;
export type ExpressedNotInterestedEvent = z.infer<typeof ExpressedNotInterestedSchema>;
export type PDFInteractedEvent = z.infer<typeof PDFInteractedSchema>;
export type ToolbarClickedEvent = z.infer<typeof ToolbarClickedSchema>;

// Type for any event data
export type EventData =
  | ActionViewedEvent
  | ActionRenderedEvent
  | ActionInteractedEvent
  | ActionRejectedEvent
  | ActionEditedEvent
  | StripeConnectOnboardingStartedEvent
  | CreateNoteFromEncounterButtonClickedEvent
  | FeedbackSubmittedEvent
  | ExpressedInterestEvent
  | ExpressedNotInterestedEvent
  | PDFInteractedEvent
  | ToolbarClickedEvent;

/**
 * Documentation for each event type
 */
export const EVENT_TYPE_METADATA: Record<EventType, { description: string }> = {
  [EventType.ACTION_VIEWED]: {
    description: 'Triggered when a user views an action',
  },
  [EventType.ACTION_RENDERED]: {
    description: 'Triggered when an action is rendered',
  },
  [EventType.ACTION_INTERACTED]: {
    description: 'Triggered when a user interacts with an action',
  },
  [EventType.ACTION_REJECTED_REASON_PROVIDED]: {
    description: 'Triggered when a user rejects an action with specific reason',
  },
  [EventType.ACTION_EDITED]: {
    description: 'Triggered when a user edits an action',
  },
  [EventType.STRIPE_CONNECT_ONBOARDING_STARTED]: {
    description: 'Triggered when a user starts the Stripe connect onboarding',
  },
  [EventType.ENCOUNTER_SUBMITTED]: {
    description: 'Triggered when a user creates a note from the encounter view',
  },
  [EventType.FEEDBACK_SUBMITTED]: {
    description: 'Triggered when a user submits thumbs up/down feedback with optional metadata',
  },
  [EventType.EXPRESSED_INTEREST]: {
    description: 'Event Data for EXPRESSED_INTEREST event - tracks fake door test clicks',
  },
  [EventType.EXPRESSED_NOT_INTERESTED]: {
    description: 'Event Data for EXPRESSED_NOT_INTERESTED event - tracks when users dismiss or decline features',
  },
  [EventType.PDF_INTERACTED]: {
    description: 'Triggered when a PDF is downloaded, viewed, or printed',
  },
  [EventType.TOOLBAR_CLICKED]: {
    description: 'Triggered when a toolbar action is clicked',
  },
};
